/**
 * Configurable Frontend Logger
 * 
 * Log levels (from least to most verbose):
 * - 'none': No logging
 * - 'error': Only errors
 * - 'warn': Errors and warnings
 * - 'info': Errors, warnings, and info
 * - 'debug': All logs including debug
 * 
 * Configuration:
 * - Build-time: REACT_APP_LOG_LEVEL environment variable
 * - Runtime: Server-side admin settings (fetched on app init)
 * 
 * Security: Log level cannot be changed client-side. Debug mode must be
 * enabled by admin on a per-user basis via server settings.
 */

const LOG_LEVELS = {
  none: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

// Get log level from environment variable only (secure - set at build time)
const getConfiguredLevel = () => {
  // Check environment variable (set at build time)
  const envLevel = process.env.REACT_APP_LOG_LEVEL;
  if (envLevel && LOG_LEVELS[envLevel] !== undefined) {
    return envLevel;
  }
  
  // Default: 'warn' in production, 'debug' in development
  return process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
};

let currentLevel = getConfiguredLevel();

// Server-controlled debug mode for specific users
let serverDebugEnabled = false;

/**
 * Initialize logger with server-side settings.
 * Called by AuthContext after user login to check if debug is enabled for this user.
 * @param {Object} userSettings - User settings from server
 */
const initFromServerSettings = (userSettings) => {
  if (userSettings && userSettings.debug_logging_enabled === true) {
    serverDebugEnabled = true;
    currentLevel = 'debug';
  }
};

const shouldLog = (level) => {
  return LOG_LEVELS[level] <= LOG_LEVELS[currentLevel];
};

// Format prefix with optional component/module name
const formatPrefix = (prefix) => {
  if (!prefix) return '';
  return `[${prefix}]`;
};

/**
 * Logger instance with configurable verbosity
 */
const logger = {
  /**
   * Debug level - most verbose, for development tracing
   * @param {string} prefix - Component or module name
   * @param {...any} args - Log arguments
   */
  debug: (prefix, ...args) => {
    if (shouldLog('debug')) {
      console.log(formatPrefix(prefix), ...args);
    }
  },

  /**
   * Info level - general information
   * @param {string} prefix - Component or module name
   * @param {...any} args - Log arguments
   */
  info: (prefix, ...args) => {
    if (shouldLog('info')) {
      console.log(formatPrefix(prefix), ...args);
    }
  },

  /**
   * Warning level - potential issues
   * @param {string} prefix - Component or module name
   * @param {...any} args - Log arguments
   */
  warn: (prefix, ...args) => {
    if (shouldLog('warn')) {
      console.warn(formatPrefix(prefix), ...args);
    }
  },

  /**
   * Error level - errors that need attention
   * @param {string} prefix - Component or module name
   * @param {...any} args - Log arguments
   */
  error: (prefix, ...args) => {
    if (shouldLog('error')) {
      console.error(formatPrefix(prefix), ...args);
    }
  },

  /**
   * Always log regardless of level (for critical startup messages)
   * @param {string} prefix - Component or module name
   * @param {...any} args - Log arguments
   */
  always: (prefix, ...args) => {
    console.log(formatPrefix(prefix), ...args);
  },

  /**
   * Group logs together (only in debug mode)
   * @param {string} label - Group label
   */
  group: (label) => {
    if (shouldLog('debug')) {
      console.group(label);
    }
  },

  /**
   * End log group
   */
  groupEnd: () => {
    if (shouldLog('debug')) {
      console.groupEnd();
    }
  },

  /**
   * Table output (only in debug mode)
   * @param {any} data - Data to display as table
   */
  table: (data) => {
    if (shouldLog('debug')) {
      console.table(data);
    }
  },

  /**
   * Get current log level
   * @returns {string} Current log level
   */
  getLevel: () => currentLevel,

  /**
   * Check if a specific level is enabled
   * @param {string} level - Level to check
   * @returns {boolean} Whether the level is enabled
   */
  isEnabled: (level) => shouldLog(level),
  
  /**
   * Check if server debug mode is enabled for this user
   * @returns {boolean} Whether server debug is enabled
   */
  isServerDebugEnabled: () => serverDebugEnabled,
  
  /**
   * Initialize from server settings (called after login)
   * @param {Object} userSettings - User settings from server
   */
  initFromServerSettings,
  
  /**
   * Reset to default level (called on logout)
   */
  reset: () => {
    serverDebugEnabled = false;
    currentLevel = getConfiguredLevel();
  },
};

export default logger;

// Named exports for convenience
export const { debug, info, warn, error, always } = logger;
